/**
 * File helpers of the Settings page (W2-13 settings-keymap): save a Blob as a download, pick a file, format sizes.
 */

import type { MessageKey } from '../../i18n/types';
import { m } from './settings.messages';

/** Saves `blob` under `fileName` through a temporary object URL. */
export function downloadBlob(blob: Blob, fileName: string): void {
  if (typeof URL.createObjectURL !== 'function') return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.append(a);
  a.click();
  a.remove();
  // Give the browser time to start the download before the URL is released.
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 30_000);
}

/** Reads a File (or Blob) as text. */
export async function readText(file: Blob): Promise<string> {
  if (typeof file.text === 'function') return file.text();
  return new Response(file).text();
}

/** Reads a File (or Blob) as bytes. */
export async function readBytes(file: Blob): Promise<Uint8Array> {
  const buf =
    typeof file.arrayBuffer === 'function' ? await file.arrayBuffer() : await new Response(file).arrayBuffer();
  return new Uint8Array(buf);
}

/** Size units (1024-based), as messages so each language writes them its own way. */
const SIZE_UNITS: readonly MessageKey[] = [m.sizeB, m.sizeKB, m.sizeMB, m.sizeGB, m.sizeTB];

/** What formatBytes needs to write a size: the translator and the locale (for the digits). */
export interface SizeText {
  readonly locale: string;
  readonly t: (key: MessageKey, params?: Readonly<Record<string, string | number>>) => string;
}

/** "0 B", "12 KB", "3.4 MB", "1.25 GB" (1024-based, locale digits, translated unit). */
export function formatBytes(bytes: number, text: SizeText): string {
  let v = Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
  let unit = 0;
  while (v >= 1024 && unit < SIZE_UNITS.length - 1) {
    v /= 1024;
    unit++;
  }
  const digits = unit === 0 ? 0 : v < 10 ? 2 : v < 100 ? 1 : 0;
  const size = new Intl.NumberFormat(text.locale, { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(
    v,
  );
  return text.t(SIZE_UNITS[unit] ?? m.sizeB, { size });
}

/** Today's date as YYYY-MM-DD (file names). */
export function dateStamp(d: Date = new Date()): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Opens the browser's file picker; resolves with the chosen file, or null when cancelled. */
export function pickFile(accept: string): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.style.display = 'none';
    const done = (file: File | null): void => {
      input.remove();
      resolve(file);
    };
    input.addEventListener(
      'change',
      () => {
        done(input.files?.[0] ?? null);
      },
      { once: true },
    );
    input.addEventListener(
      'cancel',
      () => {
        done(null);
      },
      { once: true },
    );
    document.body.append(input);
    input.click();
  });
}
